import React, { useState } from "react";

const Betting = () => {
  // State for inputs
  const [dkMoneylineA, setDkMoneylineA] = useState("");
  const [dkMoneylineB, setDkMoneylineB] = useState("");
  const [kalshiPercentYes, setKalshiPercentYes] = useState("");
  const [kalshiPercentNo, setKalshiPercentNo] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  
  // Results state
  const [results, setResults] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Parse inputs
    const mlA = parseFloat(dkMoneylineA);
    const mlB = parseFloat(dkMoneylineB);
    const total = parseFloat(totalAmount) || 0;
    const kalshiYes = parseFloat(kalshiPercentYes) / 100;
    const kalshiNo = parseFloat(kalshiPercentNo) / 100;

    // Calculate implied probabilities
    const impliedProbA = mlA < 0 ? 
      (-mlA) / (-mlA + 100) : 
      100 / (mlA + 100);
      
    const impliedProbB = mlB < 0 ? 
      (-mlB) / (-mlB + 100) : 
      100 / (mlB + 100);
    
    // Adjust probabilities for overround
    const totalProb = impliedProbA + impliedProbB;
    const adjProbA = impliedProbA * (1 / totalProb);
    const adjProbB = impliedProbB * (1 / totalProb);

    // Find optimal allocation for original trade
    let bestOriginal = null;
    let bestOriginalPositive = null;
    
    // Iterate through allocations ($0.01 increments)
    for (let dkAmt = 0; dkAmt <= total; dkAmt += 0.01) {
      const kalshiAmt = Math.max(0, total - dkAmt);
      if (dkAmt <= 0 || kalshiAmt <= 0) continue;

      // Calculate original trade
      const contracts = kalshiAmt / kalshiYes;
      const kalshiFee = Math.ceil((0.07 * contracts * kalshiYes * (1 - kalshiYes)) * 100) / 100;
      
      const dkPayout = mlA < 0 ? 
        (dkAmt * (100 / -mlA)) + dkAmt : 
        (dkAmt * (mlA / 100)) + dkAmt;
      
      const kalshiPayout = (kalshiAmt / kalshiYes) - kalshiAmt;
      
      const netProfitDkWin = dkPayout - dkAmt - kalshiAmt - kalshiFee;
      const netProfitKalshiWin = kalshiPayout - dkAmt - kalshiFee;
      
      const avgProfit = (netProfitDkWin + netProfitKalshiWin) / 2;
      const bothPositive = netProfitDkWin > 0 && netProfitKalshiWin > 0;

      // Track best allocation
      if (bothPositive) {
        if (!bestOriginalPositive || avgProfit > bestOriginalPositive.avgProfit) {
          bestOriginalPositive = {
            dkAmt,
            kalshiAmt,
            netProfitDkWin,
            netProfitKalshiWin,
            avgProfit,
            kalshiFee
          };
        }
      }
      
      if (!bestOriginal || avgProfit > bestOriginal.avgProfit) {
        bestOriginal = {
          dkAmt,
          kalshiAmt,
          netProfitDkWin,
          netProfitKalshiWin,
          avgProfit,
          kalshiFee
        };
      }
    }

    // Find optimal allocation for opposite trade
    let bestOpposite = null;
    let bestOppositePositive = null;
    
    for (let dkAmt = 0; dkAmt <= total; dkAmt += 0.01) {
      const kalshiAmt = Math.max(0, total - dkAmt);
      if (dkAmt <= 0 || kalshiAmt <= 0) continue;

      // Calculate opposite trade
      const contracts = kalshiAmt / kalshiNo;
      const kalshiFee = Math.ceil((0.07 * contracts * kalshiNo * (1 - kalshiNo)) * 100) / 100;
      
      const dkPayout = mlB < 0 ? 
        (dkAmt * (100 / -mlB)) + dkAmt : 
        (dkAmt * (mlB / 100)) + dkAmt;
      
      const kalshiPayout = (kalshiAmt / kalshiNo) - kalshiAmt;
      
      const netProfitDkWin = dkPayout - dkAmt - kalshiAmt - kalshiFee;
      const netProfitKalshiWin = kalshiPayout - dkAmt - kalshiFee;
      
      const avgProfit = (netProfitDkWin + netProfitKalshiWin) / 2;
      const bothPositive = netProfitDkWin > 0 && netProfitKalshiWin > 0;

      // Track best allocation
      if (bothPositive) {
        if (!bestOppositePositive || avgProfit > bestOppositePositive.avgProfit) {
          bestOppositePositive = {
            dkAmt,
            kalshiAmt,
            netProfitDkWin,
            netProfitKalshiWin,
            avgProfit,
            kalshiFee
          };
        }
      }
      
      if (!bestOpposite || avgProfit > bestOpposite.avgProfit) {
        bestOpposite = {
          dkAmt,
          kalshiAmt,
          netProfitDkWin,
          netProfitKalshiWin,
          avgProfit,
          kalshiFee
        };
      }
    }

    // Select final allocations
    const finalOriginal = bestOriginalPositive || bestOriginal;
    const finalOpposite = bestOppositePositive || bestOpposite;

    setResults({
      adjProbA,
      adjProbB,
      kalshiYes,
      kalshiNo,
      finalOriginal,
      finalOpposite,
      originalAllocationType: bestOriginalPositive ? "Positive Profits" : "Maximum Expected Value",
      oppositeAllocationType: bestOppositePositive ? "Positive Profits" : "Maximum Expected Value",
      calculationSteps: [
        `DraftKings Moneyline A: ${mlA} → Implied Probability: ${(impliedProbA * 100).toFixed(2)}%`,
        `DraftKings Moneyline B: ${mlB} → Implied Probability: ${(impliedProbB * 100).toFixed(2)}%`,
        `Total Probability: ${(totalProb * 100).toFixed(2)}% → Adjusted Probability A: ${(adjProbA * 100).toFixed(2)}%`,
        `Kalshi Yes: ${(kalshiYes * 100).toFixed(2)}% → Kalshi No: ${(kalshiNo * 100).toFixed(2)}%`,
        `\nOptimal Allocation for Original Trade:`,
        `• DraftKings: $${finalOriginal.dkAmt.toFixed(2)}`,
        `• Kalshi: $${finalOriginal.kalshiAmt.toFixed(2)}`,
        `\nOptimal Allocation for Opposite Trade:`,
        `• DraftKings: $${finalOpposite.dkAmt.toFixed(2)}`,
        `• Kalshi: $${finalOpposite.kalshiAmt.toFixed(2)}`
      ]
    });
  };

  return (
    <div style={{ 
      maxWidth: 900, 
      margin: "auto", 
      padding: 20, 
      background: "#fff",
      minHeight: "100vh"
    }}>
      <h2>Arbitrage Calculator</h2>
      
      <form onSubmit={handleSubmit}>
        {/* DraftKings Section */}
        <div style={{ marginBottom: 20 }}>
          <h3>DraftKings</h3>
          <div style={{ display: "flex", gap: 15, flexWrap: "wrap" }}>
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>Moneyline A:</label>
              <input
                type="number"
                value={dkMoneylineA}
                onChange={(e) => setDkMoneylineA(e.target.value)}
                placeholder="e.g., -120"
                style={{ width: 120 }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>Moneyline B:</label>
              <input
                type="number"
                value={dkMoneylineB}
                onChange={(e) => setDkMoneylineB(e.target.value)}
                placeholder="e.g., +150"
                style={{ width: 120 }}
              />
            </div>
          </div>
        </div>
        
        {/* Kalshi Section */}
        <div style={{ marginBottom: 20 }}>
          <h3>Kalshi</h3>
          <div style={{ display: "flex", gap: 15, flexWrap: "wrap" }}>
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>Yes %:</label>
              <input
                type="number"
                value={kalshiPercentYes}
                onChange={(e) => setKalshiPercentYes(e.target.value)}
                placeholder="e.g., 60"
                min="0"
                max="100"
                style={{ width: 120 }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>No %:</label>
              <input
                type="number"
                value={kalshiPercentNo}
                onChange={(e) => setKalshiPercentNo(e.target.value)}
                placeholder="e.g., 40"
                min="0"
                max="100"
                style={{ width: 120 }}
              />
            </div>
          </div>
        </div>
        
        {/* Total Amount */}
        <div style={{ marginBottom: 20 }}>
          <h3>Total Amount</h3>
          <div>
            <label style={{ display: "block", marginBottom: 5 }}>Total Amount ($):</label>
            <input
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              placeholder="100"
              step="0.01"
              min="0"
              style={{ width: 120 }}
            />
          </div>
        </div>
        
        <button type="submit" style={{ padding: "8px 16px", fontSize: "1rem" }}>
          Calculate Arbitrage
        </button>
      </form>
      
      {results && (
        <div style={{ 
          marginTop: 30, 
          background: "#f8f9fa", 
          padding: 20, 
          borderRadius: 8,
          border: "1px solid #eee"
        }}>
          <h3>Arbitrage Results</h3>
          
          <div style={{ marginBottom: 20 }}>
            <h4>Calculation Steps:</h4>
            <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
              {results.calculationSteps.map((step, index) => (
                <li key={index} style={{ marginBottom: 8 }}>• {step}</li>
              ))}
            </ul>
          </div>
          
          <div style={{ display: "flex", gap: 30, flexWrap: "wrap" }}>
            <div style={{ minWidth: 300 }}>
              <h4>Original Trade: Outcome 1 (DraftKings Wins)</h4>
              <ul>
                <li><strong>DraftKings Amount:</strong> ${results.finalOriginal.dkAmt.toFixed(2)}</li>
                <li><strong>Kalshi Amount:</strong> ${results.finalOriginal.kalshiAmt.toFixed(2)}</li>
                <li><strong>Net Profit:</strong> ${results.finalOriginal.netProfitDkWin.toFixed(2)}</li>
              </ul>
            </div>
            
            <div style={{ minWidth: 300 }}>
              <h4>Original Trade: Outcome 2 (Kalshi Wins)</h4>
              <ul>
                <li><strong>DraftKings Amount:</strong> ${results.finalOriginal.dkAmt.toFixed(2)}</li>
                <li><strong>Kalshi Amount:</strong> ${results.finalOriginal.kalshiAmt.toFixed(2)}</li>
                <li><strong>Net Profit:</strong> ${results.finalOriginal.netProfitKalshiWin.toFixed(2)}</li>
              </ul>
            </div>
          </div>
          
          <div style={{ marginTop: 20, padding: 15, background: "#eef7ff", borderRadius: 6 }}>
            <p><strong>Kalshi Fee:</strong> ${results.finalOriginal.kalshiFee.toFixed(2)} (applies to both outcomes)</p>
            <p><strong>Average Profit:</strong> ${results.finalOriginal.avgProfit.toFixed(2)}</p>
            <p><strong>Allocation Type:</strong> {results.originalAllocationType}</p>
          </div>

          {/* Opposite Trades Results */}
          <div style={{ marginTop: 30, borderTop: "1px solid #ccc", paddingTop: 20 }}>
            <h3>Opposite Trades Arbitrage Results</h3>
            <div style={{ display: "flex", gap: 30, flexWrap: "wrap" }}>
              <div style={{ minWidth: 300 }}>
                <h4>Opposite Trade: Outcome 1 (DraftKings B Wins)</h4>
                <ul>
                  <li><strong>DraftKings Amount:</strong> ${results.finalOpposite.dkAmt.toFixed(2)}</li>
                  <li><strong>Kalshi Amount:</strong> ${results.finalOpposite.kalshiAmt.toFixed(2)}</li>
                  <li><strong>Net Profit:</strong> ${results.finalOpposite.netProfitDkWin.toFixed(2)}</li>
                </ul>
              </div>
              <div style={{ minWidth: 300 }}>
                <h4>Opposite Trade: Outcome 2 (Kalshi No Wins)</h4>
                <ul>
                  <li><strong>DraftKings Amount:</strong> ${results.finalOpposite.dkAmt.toFixed(2)}</li>
                  <li><strong>Kalshi Amount:</strong> ${results.finalOpposite.kalshiAmt.toFixed(2)}</li>
                  <li><strong>Net Profit:</strong> ${results.finalOpposite.netProfitKalshiWin.toFixed(2)}</li>
                </ul>
              </div>
            </div>
            <div style={{ marginTop: 20, padding: 15, background: "#eef7ff", borderRadius: 6 }}>
              <p><strong>Kalshi Fee:</strong> ${results.finalOpposite.kalshiFee.toFixed(2)} (applies to both outcomes)</p>
              <p><strong>Average Profit:</strong> ${results.finalOpposite.avgProfit.toFixed(2)}</p>
              <p><strong>Allocation Type:</strong> {results.oppositeAllocationType}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Betting;
